import React, { useCallback, useRef, useState } from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import InputValidationTooltip from "../../common/InputValidationTooltip";
import { notAllowedSpecialCharsGlobalRegex } from "constants/validations";

interface NameFieldProps {
    register: UseFormRegisterReturn;
    error?: string;
    disabled?: boolean;
    width?: string;
    type: "first" | "last";
    id?: string;
    placeholder?: string;
    className?: string;
}

const NameField = ({
    register,
    error,
    disabled = false,
    width = "w-[165px] tablet:w-[172px]",
    type,
    id,
    placeholder,
    className = "",
}: NameFieldProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const label = type === "first" ? "First Name" : "Last Name";
    const maxLength = type === "first" ? 20 : 29;

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            const filteredValue = value.replace(notAllowedSpecialCharsGlobalRegex, "");

            if (value !== filteredValue && value.length > 0) {
                inputRef.current = e.target;
                setShowTooltip(true);

                if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);

                tooltipTimeoutRef.current = setTimeout(() => {
                    setShowTooltip(false);
                }, 2000);
            }

            e.target.value = filteredValue;

            if (register?.onChange) {
                register.onChange(e);
            }
        },
        [register]
    );

    return (
        <div className="form-control flex-1 min-w-0">
            <label className="label h-7" htmlFor={id}>
                <span className="text-base-content">{label}</span>
            </label>
            <input
                {...register}
                onChange={handleChange}
                ref={(e) => {
                    if (register?.ref) {
                        register.ref(e);
                    }
                    inputRef.current = e;
                }}
                id={id}
                type="text"
                className={`input input-bordered bg-base-100 ${width} h-9 text-sm ${
                    disabled ? "opacity-50 cursor-not-allowed" : ""
                } ${error ? "input-error" : ""} ${className}`}
                disabled={disabled}
                maxLength={maxLength}
                placeholder={placeholder}
            />
            <InputValidationTooltip message="Special characters not allowed" show={showTooltip} targetRef={inputRef} />
            {error && <p className="text-error text-xs mt-1">{error}</p>}
        </div>
    );
};

export default NameField;
